import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { useSEO } from "../hooks/useSEO";
import { Reveal, openSchedule } from "./Reveal";

// Portfolio brands with their own pages (from the original Skope site)
const PORTFOLIO_BRANDS = [
  { name: "ChaatGPT", slug: "/our-brands/chaatgpt", tag: "Authentic Indian chaat, delivery-first in Bengaluru", img: "https://framerusercontent.com/images/ldpx9tzuPb6WsZqvCtvIPfdE.png" },
  { name: "Al Mashawi Shawarma", slug: "/our-brands/al-mashawi-shawarma", tag: "Authentic Arabic shawarma from JP Nagar", img: "/assets/brands/al-mashawi.jpg" },
  { name: "Chicbun", slug: "/our-brands/chicbun", tag: "The sauciest sliders & tenders in Bangalore", img: "https://framerusercontent.com/images/p1wTLLfubnUQuu4APjqi1ZVY.png" },
  { name: "Kritunga", slug: "/our-brands/kritunga-biryani", tag: "The Palegar's cuisine — authentic Andhra biryani", img: "/assets/brands/kritunga.jpg" },
  { name: "Saladeo", slug: "/our-brands/saladeo", tag: "Fresh, fast, salad delivery in Bangalore", img: "https://framerusercontent.com/images/rZn5uXcQC7dulzO5jaOj6Kt1HFY.png" },
  { name: "Skope Café", slug: "/our-brands/skope-cafe", tag: "Your neighbourhood cloud café in JP Nagar", img: "https://framerusercontent.com/images/iqrcXcAXmVzlCSPyFwoR2i8Eak.png" },
];

// New brands now scaling with Skope (logos provided by Skope; descriptions from public listings/brand identity)
const SCALING_BRANDS = [
  { name: "Greedo", tag: "Your Everyday Fuel Station", desc: "A Bengaluru nutrition brand making hygienic, protein-first meals convenient — pairing cloud-kitchen production with IoT-enabled smart vending machines.", img: "/assets/brands/greedo.jpg" },
  { name: "BAO Bangalore", tag: "Himalayan Asian Kitchen", desc: "Bengaluru's gourmet Himalayan kitchen by chef Mahendra Thulung — flavours from Nepal, Bhutan and Tibet. Born as a cloud kitchen, grown into an award-winning quick-service concept.", img: "/assets/brands/bao-bangalore.jpg" },
  { name: "The Healthy Füd", tag: "The Good Food Way", desc: "Wholesome, balanced meals made the good-food way — clean ingredients, professional kitchens, delivered fresh.", img: "/assets/brands/the-healthy-fud.jpg" },
  { name: "Kochi Kurry Klub", tag: "Taste of Kerala", desc: "Kerala on a plate — bold, coastal curries and classics from God's Own Country, made for delivery.", img: "/assets/brands/kochi-kurry-klub.jpg" },
  { name: "Malabar Flavors", tag: "Better Food, Better Mood", desc: "Malabar-inspired flavours cooked with care — rich, aromatic and comforting, straight from the coast.", img: "/assets/brands/malabar-flavors.jpg" },
  { name: "Swanky Spoon Society", tag: "Premium Dining, Delivered", desc: "A premium food brand serving JP Nagar, Bengaluru — available on Swiggy.", img: "/assets/brands/swanky-spoon-society.jpg" },
  { name: "Slovon Bakehouse", tag: "Artisan Bakehouse", desc: "Small-batch artisan bakes — breads, bakes and patisserie made fresh from the oven.", img: "/assets/brands/slovon-bakehouse.jpg" },
  { name: "Sakkath Bites", tag: "Sakkath Means Awesome", desc: "A Bengaluru bites brand serving snackable favourites online — named after the Kannada word for 'awesome'.", img: "/assets/brands/sakkath-bites.jpg" },
  { name: "Doughpamine Kitchen", tag: "Dessert Therapy · Est. 2025", desc: "Indulgent desserts and bakes engineered for joy — a dessert-first cloud kitchen brand.", img: "/assets/brands/doughpamine-kitchen.jpg" },
  { name: "Wrap O'Clock", tag: "Wraps, Any Time", desc: "Wraps, rolls and quick bites — because any time is wrap o'clock.", img: "/assets/brands/wrap-oclock.jpg" },
];

const LogoCard = ({ brand, i, link }) => {
  const inner = (
    <>
      <div className="aspect-[4/3] bg-white border border-ink/10 flex items-center justify-center p-6 overflow-hidden">
        <img src={brand.img} alt={`${brand.name} logo`} loading="lazy"
          className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-500"
          onError={(e) => { e.currentTarget.style.display = "none"; }} />
      </div>
      <div className="pt-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-display font-semibold text-lg text-ink group-hover:text-skope transition-colors">{brand.name}</h3>
          {link && <ArrowUpRight size={16} className="text-ink/30 group-hover:text-skope transition-colors shrink-0" />}
        </div>
        <div className="mt-1 font-mono text-[10px] tracking-[0.2em] text-skope uppercase">{brand.tag}</div>
        {brand.desc && <p className="mt-2 text-sm text-ink/55 leading-relaxed">{brand.desc}</p>}
      </div>
    </>
  );
  return (
    <Reveal delay={0.04 * (i % 4)}>
      {link ? (
        <Link to={brand.slug} className="group block" data-testid={`brand-card-${brand.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{inner}</Link>
      ) : (
        <div className="group" data-testid={`brand-card-${brand.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{inner}</div>
      )}
    </Reveal>
  );
};

const BrandsPage = () => {
  useSEO({
    title: "Our Brands — Food Brands We're Scaling | Skope Kitchens",
    description:
      "From chaat and biryani to shawarma, salads, desserts and pet food — meet the food brands Skope Kitchens is building and scaling across Bengaluru and beyond.",
    canonical: "https://www.skopekitchens.com/our-brands",
  });

  return (
    <div className="bg-white min-h-screen text-ink font-body" data-testid="brands-page">
      <Navbar />
      <main className="pb-24">
        <div className="bg-ink bg-blueprint text-white pt-32 lg:pt-40 pb-12 lg:pb-16">
          <div className="max-w-[1200px] mx-auto px-6">
            <Reveal>
              <div className="flex items-center gap-3 font-mono text-[11px] tracking-[0.35em] text-skope uppercase">
                <span className="w-1.5 h-1.5 rounded-full bg-skope" />
                Our Brands
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="mt-6 font-display font-bold text-3xl lg:text-5xl tracking-tight leading-[1.1] text-white" data-testid="page-h1">
                Brands we're building &amp; scaling.
              </h1>
            </Reveal>
            <Reveal delay={0.14}>
              <p className="mt-5 text-white/55 leading-relaxed max-w-2xl">
                From chaat and biryani to shawarma, salads, desserts and even pet food — every brand here runs on
                Skope's kitchens, SOPs and delivery-first operations. And we're adding more.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="max-w-[1200px] mx-auto px-6 mt-16 lg:mt-20">
          <Reveal>
            <div className="font-mono text-[11px] tracking-[0.3em] text-skope uppercase">Now scaling with Skope</div>
          </Reveal>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10" data-testid="scaling-brands-grid">
            {SCALING_BRANDS.map((b, i) => <LogoCard key={b.name} brand={b} i={i} link={false} />)}
          </div>
        </div>

        <div className="max-w-[1200px] mx-auto px-6 mt-20 lg:mt-28">
          <Reveal>
            <div className="font-mono text-[11px] tracking-[0.3em] text-skope uppercase">Our portfolio brands</div>
          </Reveal>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10" data-testid="portfolio-brands-grid">
            {PORTFOLIO_BRANDS.map((b, i) => <LogoCard key={b.name} brand={b} i={i} link />)}
          </div>
        </div>

        <div className="max-w-[1200px] mx-auto px-6 mt-20">
          <div className="border border-skope/30 bg-ink text-white p-8 lg:p-10" data-testid="brands-cta">
            <div className="font-mono text-[10px] tracking-[0.3em] text-skope uppercase">Your Brand Here</div>
            <h2 className="mt-3 font-display font-semibold text-2xl text-white tracking-tight">Building a food brand?</h2>
            <p className="mt-3 text-white/55 leading-relaxed max-w-lg">
              Let's build the kitchen and operating system behind it — and put your brand on this wall next.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-4">
              <button onClick={() => openSchedule()} className="flex items-center justify-center gap-3 bg-skope text-ink font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-white hover:text-ink transition-colors" data-testid="brands-cta-schedule">
                Schedule a Call <ArrowRight size={14} />
              </button>
              <a href="/#calculator" className="flex items-center justify-center gap-3 border border-white/25 text-white font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 hover:border-skope hover:text-skope transition-colors" data-testid="brands-cta-calculator">
                Calculate Your Kitchen
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BrandsPage;
